import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Bouton unique du site, décliné par variante et par taille.
 *
 * Il gère nativement l'état « en cours » : `loading` désactive le bouton et
 * affiche un indicateur, sans que chaque écran ait à le réimplémenter. C'est
 * une protection concrète contre le double-clic sur le bouton de paiement.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
    primary: 'bg-brand-600 text-white hover:bg-brand-700 shadow-sm shadow-brand-600/20',
    accent: 'bg-accent-600 text-white hover:bg-accent-700 shadow-sm shadow-accent-600/20',
    secondary: 'bg-white text-ink border border-line-strong hover:bg-surface-sunken',
    ghost: 'text-ink-soft hover:bg-surface-sunken hover:text-ink',
    danger: 'bg-critical text-white hover:opacity-90',
};

const SIZES: Record<Size, string> = {
    sm: 'h-9 px-3.5 text-sm gap-1.5',
    md: 'h-11 px-5 text-sm gap-2',
    lg: 'h-13 px-7 text-base gap-2.5',
};

const BASE =
    'inline-flex items-center justify-center rounded-lg font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 whitespace-nowrap';

function classesFor(variant: Variant, size: Size, className?: string): string {
    return cn(BASE, VARIANTS[variant], SIZES[size], className);
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: Variant;
    size?: Size;
    loading?: boolean;
};

export function Button({
    variant = 'primary',
    size = 'md',
    loading = false,
    className,
    children,
    disabled,
    ...props
}: ButtonProps) {
    return (
        <button
            {...props}
            disabled={disabled || loading}
            className={classesFor(variant, size, className)}
            // `aria-busy` annonce l'attente aux lecteurs d'écran, que le
            // spinner visuel ne suffit pas à communiquer.
            aria-busy={loading}
        >
            {loading && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {children}
        </button>
    );
}

/**
 * Même apparence, mais c'est un vrai lien de navigation.
 *
 * On ne déguise pas un bouton en lien : `react-router` doit recevoir un
 * `<a href>` réel pour que le clic-milieu, le « ouvrir dans un nouvel
 * onglet » et l'indexation fonctionnent.
 */
export function ButtonLink({
    to,
    variant = 'primary',
    size = 'md',
    className,
    children,
    ...props
}: {
    to: string;
    variant?: Variant;
    size?: Size;
    className?: string;
    children: ReactNode;
} & Omit<ComponentProps<typeof Link>, 'to' | 'className' | 'children'>) {
    return (
        <Link to={to} className={classesFor(variant, size, className)} {...props}>
            {children}
        </Link>
    );
}
